import React, { useMemo, useState } from 'react';
import './style.css';

export type Column<T> = {
  key: keyof T | string;
  header: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (row: T, value: any, index: number) => React.ReactNode;
  sortable?: boolean;
};

export type ResponsiveDataGridProps<T> = {
  columns: Column<T>[];
  data?: T[];
  page?: { list?: T[]; page?: number; pageSize?: number; total?: number } | null;
  className?: string;
  initialSortKey?: string;
  initialSortDir?: 'asc' | 'desc';
  emptyMessage?: string;
  onRowClicked?: (value: T) => void;
};

const cx = (...xs: Array<string | false | null | undefined>): string =>
  xs.filter(Boolean).join(' ');

const sortBy = <T,>(arr: T[], key: string, dir: 'asc' | 'desc'): T[] => {
  const mult = dir === 'asc' ? 1 : -1;
  return [...arr].sort((a: any, b: any) => {
    const va = a?.[key];
    const vb = b?.[key];
    if (va == null && vb == null) return 0;
    if (va == null) return -1 * mult;
    if (vb == null) return 1 * mult;
    const na = Number(va);
    const nb = Number(vb);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return (na - nb) * mult;
    return String(va).localeCompare(String(vb)) * mult;
  });
};

export const ResponsiveDataGrid = <T extends Record<string, any>>({
  columns,
  data,
  page,
  className,
  initialSortKey,
  initialSortDir = 'asc',
  emptyMessage = 'Nenhum registro encontrado.',
  onRowClicked,
}: ResponsiveDataGridProps<T>): JSX.Element => {
  const [sortKey, setSortKey] = useState<string | undefined>(initialSortKey);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSortDir);

  const rowsSource = useMemo<T[]>(() => {
    if (page && Array.isArray(page.list)) return page.list as T[];
    if (Array.isArray(data)) return data as T[];
    return [] as T[];
  }, [data, page]);

  const processed = useMemo(() => {
    let rows = rowsSource;
    if (sortKey) rows = sortBy(rows, sortKey, sortDir);
    return rows;
  }, [rowsSource, sortKey, sortDir]);

  const gridTemplate = useMemo(() => {
    const cols = columns.map(c => c.width || 'minmax(140px, 1fr)').join(' ');
    return { gridTemplateColumns: cols } as React.CSSProperties;
  }, [columns]);

  const toggleSort = (key: string): void => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
    } else {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    }
  };

  return (
    <section className={cx('rdg-root', className)}>
      <div role="table" aria-colcount={columns.length} className="rdg-desktop">
        <div role="row" className="rdg-header" style={gridTemplate}>
          {columns.map((col, i) => (
            <div
              key={String(col.key) + i}
              role="columnheader"
              className={cx(
                'rdg-th',
                col.align === 'center' && 'rdg-center',
                col.align === 'right' && 'rdg-right',
              )}
              onClick={() => col.sortable && toggleSort(String(col.key))}
            >
              <span>{col.header}</span>
            </div>
          ))}
        </div>

        {processed.length === 0 ? (
          <div className="rdg-empty">{emptyMessage}</div>
        ) : (
          processed.map((row, idx) => (
            <div
              key={idx}
              role="row"
              className={cx('rdg-tr', idx % 2 === 0 ? 'rdg-even' : 'rdg-odd')}
              style={gridTemplate}
              onClick={() => (onRowClicked !== undefined ? onRowClicked(row) : undefined)}
            >
              {columns.map((col, i) => {
                const value = row[col.key as keyof typeof row];
                return (
                  <div
                    key={String(col.key) + i}
                    role="cell"
                    className={cx(
                      'rdg-td',
                      col.align === 'center' && 'rdg-center',
                      col.align === 'right' && 'rdg-right',
                    )}
                  >
                    {col.render ? col.render(row, value, idx) : String(value)}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Mobile */}
      <div className="rdg-mobile">
        {processed.length === 0 ? (
          <div className="rdg-empty">{emptyMessage}</div>
        ) : (
          <div className="rdg-cards">
            {processed.map((row, idx) => (
              <div key={idx} className="rdg-card">
                {columns.map((col, i) => {
                  const value = row[col.key as keyof typeof row];
                  return (
                    <div key={String(col.key) + i} className="rdg-card-row">
                      <div className="rdg-card-label">{col.header}</div>
                      <div className="rdg-card-value">
                        {col.render ? col.render(row, value, idx) : String(value)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
